"use client";

import { Session } from "next-auth";
import Image from "next/image";
import { UserProfile } from "@/utils/types";
import { updateProfileService } from "@/services/profile.service.ts";
import { isGoogleProfile, isDiscordProfile } from "./PersonalInfoSection";

interface LinkedAccountsSectionProps {
  profileData: UserProfile | null;
  csrfToken: string;
  session: Session | null;
  onProfileUpdated: () => void;
}

const ghostButtonStyle: React.CSSProperties = {
  fontSize: "10px",
  fontWeight: 500,
  letterSpacing: "0.08em",
  color: "#888",
  background: "#141414",
  border: "1px solid #222",
  borderRadius: "7px",
  padding: "6px 14px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  cursor: "pointer",
  transition: "border-color 0.15s, color 0.15s",
};

export function LinkedAccountsSection({
  profileData,
  csrfToken,
  session,
  onProfileUpdated,
}: LinkedAccountsSectionProps) {
  function linkAccount(provider: string) {
    if (!session?.user.accessToken) {
      console.error("[LinkedAccountsSection] No session token found, can't link account");
      return;
    }
    window.location.href = `/api/auth/oauth/start?provider=${provider}&state=${encodeURIComponent(csrfToken)}`;
  }

  function renderContent() {
    if (!profileData || !Array.isArray(profileData.linkedAccounts)) return null;

    const isProviderLinked = (prov: string) =>
      profileData.linkedAccounts.some((a) => a.provider === prov && Boolean(a.providerId));

    if (!isProviderLinked("google") && !isProviderLinked("discord")) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px 0",
            gap: "12px",
          }}
        >
          <div style={{ fontSize: "24px", opacity: 0.2 }}>&#128279;</div>
          <p style={{ fontSize: "11px", color: "#4a4a4a", margin: 0 }}>No social accounts linked</p>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {profileData.linkedAccounts.map((acct, idx) => {
          const acctProfile = acct.providerProfile;

          let displayName = "";
          if (acct.provider === "google" && isGoogleProfile(acctProfile) && acctProfile.name) {
            displayName = acctProfile.name;
          } else if (acct.provider === "discord" && isDiscordProfile(acctProfile) && acctProfile.username) {
            displayName = acctProfile.username;
          } else if (acct.provider === "google" && isGoogleProfile(acctProfile)) {
            displayName = acctProfile.sub || acctProfile.email || "";
          } else if (isDiscordProfile(acctProfile)) {
            displayName = acctProfile.username || "";
          }

          let avatarUrl = "";
          if (acct.provider === "google" && isGoogleProfile(acctProfile) && acctProfile.picture) {
            avatarUrl = acctProfile.picture;
          } else if (acct.provider === "discord" && isDiscordProfile(acctProfile) && acctProfile.avatar) {
            const id = acct.providerId as string;
            if (acctProfile.avatar && id) {
              avatarUrl = `https://cdn.discordapp.com/avatars/${id}/${acctProfile.avatar}.png`;
            }
          }

          return (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {avatarUrl ? (
                  <Image
                    src={avatarUrl}
                    alt={`${acct.provider} avatar`}
                    width={24}
                    height={24}
                    style={{ borderRadius: "50%", width: "24px", height: "24px" }}
                  />
                ) : (
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "#1c1c1c",
                    }}
                  />
                )}
                <div>
                  <p style={{ fontSize: "11px", fontWeight: 500, color: "#c8c3ba", margin: 0, textTransform: "capitalize" }}>
                    {acct.provider}
                  </p>
                  {displayName && (
                    <p style={{ fontSize: "10px", color: "#4a4a4a", marginTop: "1px" }}>
                      {displayName}
                    </p>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: "6px" }}>
                {avatarUrl && (
                  <button
                    onClick={() => {
                      void (async () => {
                        try {
                          await updateProfileService({ avatar: avatarUrl });
                          onProfileUpdated();
                        } catch (err) {
                          console.error("[LinkedAccountsSection] Error setting avatar from provider:", err);
                        }
                      })();
                    }}
                    style={ghostButtonStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#272727";
                      e.currentTarget.style.color = "#d4cfc6";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#222";
                      e.currentTarget.style.color = "#888";
                    }}
                  >
                    USE PICTURE
                  </button>
                )}
                {Object.keys(acct.providerProfile).length === 0 && (
                  <button
                    onClick={() => { linkAccount(acct.provider); }}
                    style={ghostButtonStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#272727";
                      e.currentTarget.style.color = "#d4cfc6";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#222";
                      e.currentTarget.style.color = "#888";
                    }}
                  >
                    RE-AUTHORIZE
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div style={{ display: "flex", gap: "6px", paddingTop: "6px" }}>
          {!isProviderLinked("google") && (
            <button
              onClick={() => { linkAccount("google"); }}
              style={ghostButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#272727";
                e.currentTarget.style.color = "#d4cfc6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#222";
                e.currentTarget.style.color = "#888";
              }}
            >
              LINK GOOGLE
            </button>
          )}
          {!isProviderLinked("discord") && (
            <button
              onClick={() => { linkAccount("discord"); }}
              style={ghostButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#272727";
                e.currentTarget.style.color = "#d4cfc6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#222";
                e.currentTarget.style.color = "#888";
              }}
            >
              LINK DISCORD
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#0e0e0e",
        border: "1px solid #1c1c1c",
        borderRadius: "14px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid #1c1c1c",
        }}
      >
        <div>
          <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", margin: 0 }}>
            SOCIAL ACCOUNTS
          </p>
          <p style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.02em", marginTop: "2px" }}>
            Linked OAuth providers
          </p>
        </div>
        <button
          onClick={() => { linkAccount("google"); }}
          style={ghostButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#272727";
            e.currentTarget.style.color = "#d4cfc6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#222";
            e.currentTarget.style.color = "#888";
          }}
        >
          LINK ACCOUNT
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: "18px 20px" }}>
        {renderContent()}
      </div>
    </div>
  );
}
